// Seal helpers — a container may carry several seals (line / shipper / customs).
// The data model is container.seal_numbers = [{ number, type }]. For backward
// compatibility older jobs may only have container.seal_number (a single string),
// so every reader goes through here.

export const SEAL_TYPES = ["Line", "Shipper", "Customs", "Other"];

// Normalized array of { number, type } for a container, tolerating legacy data.
export function sealList(c) {
  if (!c) return [];
  const arr = Array.isArray(c.seal_numbers) ? c.seal_numbers : null;
  if (arr && arr.length) {
    return arr
      .map((s) =>
        typeof s === "string"
          ? { number: s.trim(), type: "Line" }
          : { number: (s.number || "").trim(), type: s.type || "Line" }
      )
      .filter((s) => s.number);
  }
  const single = (c.seal_number || "").trim();
  return single ? [{ number: single, type: "Line" }] : [];
}

// Raw editor rows — like sealList but KEEPS empty entries so the editor can show a
// blank input to type into. Falls back to the legacy single seal, then to one empty row.
export function sealRows(c) {
  if (!c) return [{ number: "", type: "Line" }];
  const arr = Array.isArray(c.seal_numbers) ? c.seal_numbers : null;
  if (arr && arr.length) {
    return arr.map((s) =>
      typeof s === "string"
        ? { number: s, type: "Line" }
        : { number: s.number || "", type: s.type || "Line" }
    );
  }
  const single = c.seal_number || "";
  return single ? [{ number: single, type: "Line" }] : [{ number: "", type: "Line" }];
}

// The carrier/line seal — what a BL usually prints as "LINE SEAL NO.".
export function lineSeal(c) {
  const list = sealList(c);
  const line = list.find((s) => (s.type || "").toLowerCase() === "line");
  return (line || list[0])?.number || "";
}

// Seals grouped by role for BL/doc display:
//   { carrier: "082081", shipper: ["SK60320"], customs: [...], other: [...] }
// The Line seal is the carrier seal. Extra Line seals fall into `other`.
export function sealsByType(c) {
  const list = sealList(c);
  const of = (t) => list.filter((s) => (s.type || "").toLowerCase() === t).map((s) => s.number);
  const lines = of("line");
  return {
    carrier: lines[0] || "",
    shipper: of("shipper"),
    customs: of("customs"),
    other: [...lines.slice(1), ...of("other")],
  };
}

// Human-readable list of all seals, e.g. "ML-DL883712, SH-0099 (Shipper)".
// Non-line seals are annotated with their type so docs stay unambiguous.
export function sealsText(c, { sep = ", " } = {}) {
  const list = sealList(c);
  if (!list.length) return "";
  return list
    .map((s) =>
      (s.type || "").toLowerCase() === "line" ? s.number : `${s.number} (${s.type})`
    )
    .join(sep);
}
